import { Component, Element, forceUpdate, h, Host, Prop, State, Watch } from "@stencil/core";

import throttle from "lodash/throttle";
import { hasSlot } from "../../utils/utils";

/**
 * @slot - Slot for content that should stick to chat place
 * @slot - Slot for content that should stick to scroll-up place

 */
@Component({
  tag: "stzh-sticky-actions",
  styleUrl: "stzh-sticky-actions.scss",
  shadow: true,
})
export class StzhStickyActions {
  /** Query used to get pagetitle element (will show scrollup when passed). */
  @Prop() pagetitleQuery: string | (() => HTMLElement) =
    "stzh-pagetitle, stzh-pagetitle-hero, stzh-pagetitle-home, stzh-pi-pagetitle, stzh-pi-pagetitle, stzh-homepage-service-highlights";

  @State() hasActiveScrollup = false;
  @State() isSlotted = true;

  @Element() element: HTMLStzhStickyActionsElement;

  @Watch("hasActiveScrollup")
  hasActiveScrollupWatcher(newValue: boolean) {
    document.documentElement.style.setProperty("--stzh-sticky-actions-scrollup-is-active", newValue ? "1" : "0");
    document.documentElement.style.setProperty("--stzh-sticky-actions-scrollup-is-not-active", newValue ? "0" : "1");
  }

  private observer: IntersectionObserver;
  private resizeObserver: ResizeObserver;
  private scrollupElement: HTMLElement;

  private observePagetitle = (entries: IntersectionObserverEntry[]) => {
    this.hasActiveScrollup = !entries[0].isIntersecting;
  };

  private updateScrollupWidth = () => {
    if (this.scrollupElement && this.element.isConnected) {
      const { width } = this.scrollupElement.getBoundingClientRect();
      document.documentElement.style.setProperty("--stzh-sticky-actions-scrollup-width", `${width}px`);
    }
  };

  private handleScrollupResize = throttle(this.updateScrollupWidth, 100);

  componentDidLoad() {
    this.isSlotted = !!this.element.assignedSlot;
    const pagetitle =
      typeof this.pagetitleQuery === "string" ? document.querySelector(this.pagetitleQuery) : this.pagetitleQuery();

    if (pagetitle) {
      this.observer = new IntersectionObserver(this.observePagetitle, { rootMargin: "0px" });
      this.observer.observe(pagetitle);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateScrollupWidth();

        this.resizeObserver = new ResizeObserver(this.handleScrollupResize);
        this.resizeObserver.observe(this.scrollupElement);
      });
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  render() {
    const chatSlotUsed = hasSlot(this.element, "chat");
    const scrollupSlotUsed = hasSlot(this.element, "scrollup");

    const classes = {
      "stzh-sticky-actions": true,
      "has-chat": chatSlotUsed,
      "has-scrollup": scrollupSlotUsed,
      "has-active-scrollup": this.hasActiveScrollup,
      "is-not-slotted": !this.isSlotted,
    };

    return (
      <Host>
        <div class={classes} data-stzh-sticky-width-container>
          <stzh-sticky
            name="actions"
            class="stzh-sticky-actions__sticky"
            position="fixed"
            variant="transparent"
            disableStickyTop={true}
          >
            <div class="stzh-sticky-actions__container">
              <div class="stzh-sticky-actions__chat-wrapper">
                <slot name="chat" onSlotchange={() => forceUpdate(this.element)}></slot>
              </div>
              <div ref={el => (this.scrollupElement = el as HTMLElement)} class="stzh-sticky-actions__scrollup-wrapper">
                <slot name="scrollup" onSlotchange={() => forceUpdate(this.element)}></slot>
              </div>
            </div>
          </stzh-sticky>
        </div>
      </Host>
    );
  }
}
